// Reads and writes the PER-PLAN module catalog (L3).
//
//   GET   /get-modules/{project_id}/{account_id}/{cognito_id}
//   PATCH /set-module/{project_id}/{account_id}/{cognito_id}
//         body { module_key, enabled, acknowledged? }
//
// Both live on the projects service, so they use apiProjectsGet /
// apiProjectsPatch and carry the session ids in the path, matching every other
// project-scoped action in this folder.
//
// A failed READ deliberately leaves the previous snapshot in place and does not
// mark the plan fetched. useModuleEnabled reads an unfetched plan as
// everything-on, so a network blip can never make features vanish from
// navigation — the same fail-open the backend's getPlanModules uses when its
// own read throws.

import { apiProjectsGet, apiProjectsPatch } from '../../utilities/apiClient';
import {
  SET_PLAN_MODULES,
  SET_PLAN_MODULES_LOADING,
  SET_PLAN_MODULE_PENDING,
} from '../constants';
import { getSessionIds } from './_sessionIds';

export const fetchPlanModules = (projectId: string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!projectId || !accountId || !cognitoId) return;

  dispatch({ type: SET_PLAN_MODULES_LOADING, payload: { projectId, loading: true } });
  try {
    const res = await apiProjectsGet(`/get-modules/${projectId}/${accountId}/${cognitoId}`);
    const p = res?.payload;
    if (!p || !Array.isArray(p.modules)) {
      // A 200 with an unexpected body is a failed read, not an empty catalog.
      // Storing it as empty would render "this plan has no features" and, worse,
      // mark the plan fetched — which flips every gate from on to off.
      dispatch({ type: SET_PLAN_MODULES_LOADING, payload: { projectId, loading: false } });
      return;
    }
    dispatch({
      type: SET_PLAN_MODULES,
      payload: {
        projectId,
        modules: p.modules,
        categories: p.categories,
        core: p.core,
        canManage: p.can_manage,
        manageVia: p.manage_via,
        isMember: p.is_member,
        memberStateKnown: p.member_state_known,
      },
    });
  } catch {
    dispatch({ type: SET_PLAN_MODULES_LOADING, payload: { projectId, loading: false } });
  }
};

/**
 * Flip one module for one plan. Resolves true only when the write landed.
 *
 * There is deliberately no optimistic update. A refused write (not an
 * administrator, acknowledgement missing, plan not in this account) must not
 * leave the switch showing a state the server rejected. Re-reading the catalog
 * afterwards also picks up the permission twin the server grants as a side
 * effect of a genuine off-to-on transition.
 */
export const setPlanModule =
  (projectId: string, moduleKey: string, enabled: boolean, acknowledged?: boolean) =>
  async (dispatch: any): Promise<boolean> => {
    const { accountId, cognitoId } = getSessionIds();
    if (!projectId || !moduleKey || !accountId || !cognitoId) return false;

    dispatch({ type: SET_PLAN_MODULE_PENDING, payload: { projectId, key: moduleKey, pending: true } });
    return apiProjectsPatch(
      `/set-module/${projectId}/${accountId}/${cognitoId}`,
      {
        module_key: moduleKey,
        enabled,
        // Only ever sent as an explicit true. The server requires it for any
        // module with requiresAck, and sending `false` where it means "not
        // asked" would read as a deliberate refusal.
        ...(acknowledged ? { acknowledged: true } : {}),
      },
    ).then((res) => {
      if (!res?.success) return false;
      return dispatch(fetchPlanModules(projectId)).then(() => true);
    }).catch(() => false).finally(() => {
      // Only this write owns its pending marker. A refresh can fail or finish
      // while another module is still saving.
      dispatch({ type: SET_PLAN_MODULE_PENDING, payload: { projectId, key: moduleKey, pending: false } });
    });
  };
